import { buildCreateLimitOffsetPaginatorFlow, createApi, createBaseQuery } from '@adv-frontend/modules-query';
import { apiV1Request } from '#shared/api/requests';
import {
    type OpenApiGetAlertsResponseSuccess,
    type OpenApiGetAlertsSearchParams,
    OpenApiGetAlertsUrl,
    type OpenApiResolveAlertRequest,
    type OpenApiResolveAlertResponseSuccess,
    OpenApiResolveAlertUrl,
} from '#shared/api/schema';

/** Общий размер первой страницы для автопоказа и списка, позволяющий переиспользовать query-кеш. */
export const DASHBOARD_ALERTS_PAGE_SIZE = 25;

const DashboardAlertsTag = 'DashboardAlerts' as const;

const dashboardAlertsListTag = { type: DashboardAlertsTag, id: 'dashboardAlertsList' };

const api = createApi({
    name: 'entities/dashboardAlert/api/index',
    baseQuery: createBaseQuery(apiV1Request),
    tagTypes: [DashboardAlertsTag],
    endpoints: (builder) => {
        return {
            getAlerts: builder.query<OpenApiGetAlertsResponseSuccess, OpenApiGetAlertsSearchParams>({
                query: (params) => {
                    return {
                        url: OpenApiGetAlertsUrl,
                        method: 'GET',
                        params: params,
                    };
                },
                providesTags: (result, error) => {
                    if (error || !result) {
                        return [];
                    }

                    return [
                        dashboardAlertsListTag,
                        ...result.alerts.map((alert) => {
                            return { type: DashboardAlertsTag, id: alert.id };
                        }),
                    ];
                },
                keepUnusedDataFor: 0,
            }),
            resolveAlert: builder.mutation<OpenApiResolveAlertResponseSuccess, OpenApiResolveAlertRequest>({
                query: (payload) => {
                    return {
                        url: OpenApiResolveAlertUrl,
                        method: 'POST',
                        body: payload,
                    };
                },
                invalidatesTags: (_, error, args) => {
                    if (error) {
                        return [];
                    }

                    return [dashboardAlertsListTag, { type: DashboardAlertsTag, id: args.id }];
                },
            }),
        };
    },
});

export const useDashboardAlertsLazy = api.useLazyGetAlertsQuery;
export const resolveDashboardAlert = api.endpoints.resolveAlert.mutate;

/**
 * Параметры запроса списка уведомлений для датапровайдера.
 * Пагинация задаётся самим провайдером (DASHBOARD_ALERTS_PAGE_SIZE), поэтому передавать
 * page/per_page снаружи нельзя — они всё равно будут перетёрты в getQueryArg
 */
type DashboardAlertsDataProviderQueryArg = Omit<OpenApiGetAlertsSearchParams, 'page' | 'per_page'>;

/*
 * Фабрика flow-based датапровайдера списка уведомлений.
 * Провайдер создаётся вне рендера (в useMemo у потребителя) с мемоизированными queryParams.
 * @kind apiMethod
 */
export const getDashboardAlertsDataProvider = api.endpoints.getAlerts.createDataProvider({
    getPaginatorFactory: () =>
        buildCreateLimitOffsetPaginatorFlow({
            limit: DASHBOARD_ALERTS_PAGE_SIZE,
        }),
    getTotalCount: (data) => data.total_count || 0,
    getQueryArg: ({ limit, offset }, queryArg: DashboardAlertsDataProviderQueryArg) => {
        return {
            ...queryArg,
            per_page: limit,
            page: Math.ceil((offset + limit) / limit),
        };
    },
    getRowByIndex: (data, chunkRowIndex) => data.alerts[chunkRowIndex],
    getRowInvalidatedBy: (row) => [{ type: DashboardAlertsTag, id: row.id }],
});
